import React, { useState } from 'react';
import {
  Checkbox,
  Box,
  Typography,
  MenuItem,
  Select,
  ListItemText,
  Chip,
  Tooltip,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTheme } from '@mui/material/styles';
import {
  buildControlMenuProps,
  getControlTokens,
} from '../common/controls';

const FilterTableFields = ({
  selectedColumns,
  handleColumnsChange,
  allColDefs,
  setSelectedColumns,
  isProUser = false,
  onUpgradeClick,
  allowMulti = false,
  placeholderText = '',
  label = '',
  showSelectedValue = true,
  expandAllByDefault = false,
  excludeGroups = [],
  flattenGroups = false,
  startAllCollapsed = false,
  selectedCountLabel = 'Metrics',
  controlContext = 'filterBar',
  menuContext = controlContext,
}) => {
  const theme = useTheme();
  const tokens = getControlTokens(theme, controlContext);
  
  // Group by 'group'
  const groupedColDefs = flattenGroups
    ? { Ungrouped: allColDefs }
    : allColDefs.reduce((acc, colDef) => {
        const group = colDef.group || 'Ungrouped';
        if (!acc[group]) acc[group] = [];
        acc[group].push(colDef);
        return acc;
      }, {});

  // Expand groups based on props: startAllCollapsed overrides everything
  const initialExpandedGroups = Object.keys(groupedColDefs).reduce((acc, group) => {
    // Skip excluded groups
    if (excludeGroups.includes(group)) {
      return acc;
    }

    // If startAllCollapsed is true, all groups start collapsed
    if (startAllCollapsed) {
      acc[group] = false;
      return acc;
    }

    // Skip "Company Statistics" by default
    if (group === "Company Statistics" && !expandAllByDefault) {
      acc[group] = false; // explicitly collapsed
      return acc;
    }

    if (expandAllByDefault) {
      acc[group] = true;
    } else {
      const groupHasSelected = groupedColDefs[group].some((col) =>
        selectedColumns.includes(col.field)
      );
      acc[group] = groupHasSelected;
    }
    return acc;
  }, {});
  const [expandedGroups, setExpandedGroups] = useState(initialExpandedGroups);

  const handleExpandToggle = (group) => {
    setExpandedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  const canSelect = (colDef) => colDef.field !== 'Company Name';

  // --- selection helpers (respect allowMulti) ---
  const nextSelection = (prev, isChecked, field) => {
    if (allowMulti) {
      return isChecked ? Array.from(new Set([...prev, field])) : prev.filter((f) => f !== field);
    }
    // single-select mode
    if (isChecked) return [field];
    return prev.filter((f) => f !== field);
  };

  const propagate = (next) => {
    setSelectedColumns(next);
    handleColumnsChange({ target: { value: next } });
  };



  const handleMenuItemClick = (colDef) => {
    const isChecked = selectedColumns.includes(colDef.field);

    if (!canSelect(colDef)) {
      onUpgradeClick?.(colDef);
      return;
    }

    if (allowMulti) {
      // multi-select: toggle on/off
      const toggledChecked = !isChecked;
      propagate(nextSelection(selectedColumns, toggledChecked, colDef.field));
    } else {
      // single-select: only change if not already selected
      if (!isChecked) {
        propagate([colDef.field]);
      }
    }
  };

  const renderLabelWithPro = (colDef) => {
    const label = (
      <Typography
        sx={{
          color: theme.palette.text.secondary,
          fontSize: '12px',
          lineHeight: 1.2,
        }}
        variant="body2"
      >
        {colDef.field}
      </Typography>
    );

    if (colDef.proMetric && !isProUser) {
      const chip = (
        <Chip
          label="PRO"
          size="small"
          variant="outlined"
          sx={{
            color: theme.palette.primary.main,
            fontSize: '8px',
            ml: 0.75,
            fontWeight: 'bold',
            textTransform: 'uppercase',
            height: 14,
            '& .MuiChip-label': { px: 0.5 },
          }}
        />
      );

      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {label}
          <Tooltip title="Available on SoftwareIQ Core">
            <Box sx={{ display: 'inline-flex' }}>{chip}</Box>
          </Tooltip>
        </Box>
      );
    }

    return label;
  };

  // Count selected items per group for badges
  const getGroupSelectedCount = (group) => {
    if (!groupedColDefs[group]) return 0;
    return groupedColDefs[group].filter((col) =>
      selectedColumns.includes(col.field)
    ).length;
  };

  return (
    <Box
      size="small"
      sx={{
        height: tokens.height,
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        marginTop: '0px',
      }}
    >
      {label && (
        <Typography
          variant="body2"
          sx={{
            fontSize: '10px',
            color: theme.palette.text.secondary,
            mr: 1,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {label}
        </Typography>
      )}
      <Select
        size="small"
        sx={{
          width: '100%',
          backgroundColor: tokens.surface,
          height: tokens.height,
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
          },
          '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: '2px',
            },
          },
        }}
        multiple
        value={selectedColumns}
        onChange={(e) => handleColumnsChange(e)}
        renderValue={(selected) => {
          let textToShow;

          if (!showSelectedValue) {
            // If showSelectedValue is false, always show placeholder
            textToShow = placeholderText || 'Select Columns';
          } else if (selected.length === 0) {
            textToShow = placeholderText || 'Select...';
          } else if (selected.length === 1) {
            // Single selection: show the metric name
            textToShow = selected[0];
          } else {
            // Multiple selections: show count
            textToShow = `${selected.length} ${selectedCountLabel} Selected`;
          }

          return (
            <Typography
              variant="body2"
              sx={{
                fontSize: tokens.fontSize,
                display: 'flex',
                alignItems: 'center',
                pt: '3px',
                color: theme.palette.text.secondary,
              }}
            >
              {textToShow}
            </Typography>
          );
        }}
        MenuProps={buildControlMenuProps(theme, menuContext)}
      >
        {/* Ungrouped */}
        {groupedColDefs['Ungrouped'] &&
          groupedColDefs['Ungrouped'].map((colDef) => {
            const disabledForced = colDef.field === 'Company Name';
            const disabled = disabledForced;
            const isSelected = selectedColumns.includes(colDef.field);

            return (
              <MenuItem
                key={colDef.field}
                value={colDef.field}
                disabled={disabledForced}
                sx={{
                  minHeight: '26px',
                  height: '26px',
                  py: 0.25,
                  px: 1.5,
                  justifyContent: 'space-between',
                  backgroundColor: isSelected
                    ? theme.palette.action.selected
                    : 'transparent',
                  borderLeft: isSelected
                    ? `2px solid ${theme.palette.primary.main}`
                    : '2px solid transparent',
                  opacity: disabledForced ? 0.5 : 1,
                  cursor: 'pointer',
                  transition: 'background-color 0.1s ease',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
                onClick={() => handleMenuItemClick(colDef)}
              >
                <ListItemText
                  primary={renderLabelWithPro(colDef)}
                  sx={{ my: 0 }}
                />
                <Checkbox
                  size="small"
                  checked={isSelected || colDef.field === 'Company Name'}
                  disabled={disabled}
                  sx={{
                    p: 0,
                    ml: 0.5,
                    opacity: disabled ? 0.5 : 1,
                    '& .MuiSvgIcon-root': { fontSize: 16 },
                  }}
                />
              </MenuItem>
            );
          })}

        {/* Grouped (no Fragments — use flatMap to return arrays) */}
        {Object.keys(groupedColDefs)
          .filter((group) => group !== 'Ungrouped' && !excludeGroups.includes(group))
          .flatMap((group, groupIndex, filteredGroups) => {
            const selectedCount = getGroupSelectedCount(group);
            const isExpanded = expandedGroups[group];

            const header = (
              <Box
                key={`${group}-header`}
                role="button"
                aria-expanded={isExpanded}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleExpandToggle(group);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  height: '32px',
                  px: 1.5,
                  cursor: 'pointer',
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.03)'
                    : 'rgba(0, 0, 0, 0.02)',
                  borderTop: groupIndex > 0
                    ? `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`
                    : 'none',
                  transition: 'background-color 0.1s ease',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.06)'
                      : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleExpandToggle(group);
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ChevronRightIcon
                    sx={{
                      fontSize: 16,
                      color: theme.palette.text.secondary,
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.15s ease',
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: 600,
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                    }}
                  >
                    {group}
                  </Typography>
                </Box>
                {selectedCount > 0 && (
                  <Chip
                    label={selectedCount}
                    size="small"
                    sx={{
                      height: 16,
                      minWidth: 16,
                      ml: 1.5,
                      fontSize: '9px',
                      fontWeight: 'bold',
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      '& .MuiChip-label': {
                        px: 0.5,
                      },
                    }}
                  />
                )}
              </Box>
            );

            // Always render children but hide when collapsed to prevent Select DOM change issues
            const children = groupedColDefs[group].map((colDef, index) => {
                  const disabledForced = colDef.field === 'Company Name';
                  const disabled = disabledForced;
                  const isSelected = selectedColumns.includes(colDef.field);

                  // Hide items when group is collapsed
                  if (!isExpanded) {
                    return (
                      <MenuItem
                        key={colDef.field}
                        value={colDef.field}
                        sx={{ display: 'none' }}
                      />
                    );
                  }

                  return (
                    <MenuItem
                      key={colDef.field}
                      value={colDef.field}
                      disabled={disabledForced}
                      sx={{
                        minHeight: '26px',
                        height: '26px',
                        py: 0.25,
                        pl: 3.5,
                        pr: 1.5,
                        justifyContent: 'space-between',
                        backgroundColor: isSelected
                          ? theme.palette.mode === 'dark'
                            ? 'rgba(145, 166, 205, 0.16)'
                            : 'rgba(63, 100, 168, 0.16)'
                          : 'transparent',
                        borderLeft: isSelected
                          ? `2px solid ${theme.palette.primary.main}`
                          : '2px solid transparent',
                        cursor: 'pointer',
                        transition: 'background-color 0.1s ease',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleMenuItemClick(colDef);
                      }}
                    >
                      <ListItemText
                        primary={renderLabelWithPro(colDef)}
                        sx={{ my: 0 }}
                      />
                      <Checkbox
                        size="small"
                        checked={isSelected || colDef.field === 'Company Name'}
                        disabled={disabled}
                        sx={{
                          p: 0,
                          ml: 0.5,
                          opacity: disabled ? 0.5 : 1,
                          '& .MuiSvgIcon-root': { fontSize: 16 },
                        }}
                      />
                    </MenuItem>
                  );
                });

            return [header, ...children]; // array children are valid for Menu
          })}
      </Select>
    </Box>
  );
};

export default FilterTableFields;
